/**
 * O que faz: consulta a origem Meta Ads de um lead e acompanha identificações pendentes.
 * Onde usa: seção OrigemAnuncioLead no chat, Pipeline e Contatos.
 * Conexões: React Query, RPC get_lead_meta_ad_attributions e validação Zod.
 * Tabelas: SELECT indireto de lead_ad_attributions, restrito à clínica e ao lead abertos.
 * Data: 30/08/2026.
 */
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';

const identificadorSchema = z.string().uuid();
const textoOpcionalSchema = z.string().trim().nullable();

// Valida também a resposta: um contrato incompleto não pode aparecer como origem confirmada.
// O objeto descarta campos extras para impedir que dados internos entrem no cache da interface.
const atribuicaoSchema = z.object({
  id: identificadorSchema,
  referencia_anuncio_origem: textoOpcionalSchema,
  titulo_anuncio: textoOpcionalSchema,
  url_anuncio: textoOpcionalSchema,
  plataforma: textoOpcionalSchema,
  entrada_em: textoOpcionalSchema,
  criado_em: z.string(),
  meta_ad_account_id: textoOpcionalSchema,
  meta_ad_id: textoOpcionalSchema,
  meta_ad_name: textoOpcionalSchema,
  meta_adset_id: textoOpcionalSchema,
  meta_adset_name: textoOpcionalSchema,
  meta_campaign_id: textoOpcionalSchema,
  meta_campaign_name: textoOpcionalSchema,
  meta_match_status: z.enum(['not_available', 'pending', 'matched', 'unmatched', 'error']),
  meta_resolved_at: textoOpcionalSchema,
});

const atribuicoesSchema = z.array(atribuicaoSchema).max(20);
export type LeadMetaAdAttribution = z.infer<typeof atribuicaoSchema>;

export const leadMetaAdAttributionKeys = {
  lead: (clinicaId: string | null | undefined, leadId: string | null | undefined) =>
    ['lead-meta-ad-attributions', clinicaId ?? null, leadId ?? null] as const,
};

export function useLeadMetaAdAttributions(
  clinicaId: string | null | undefined,
  leadId: string | null | undefined,
  ativo = true,
) {
  // Evita requisições sem contexto ou com IDs inválidos; a RPC repete a proteção no servidor.
  const contextoValido = identificadorSchema.safeParse(clinicaId).success
    && identificadorSchema.safeParse(leadId).success;
  const habilitado = ativo && contextoValido;

  const consulta = useQuery<LeadMetaAdAttribution[], Error>({
    // A chave nunca reaproveita a origem do lead anterior durante troca de clínica/conversa.
    queryKey: leadMetaAdAttributionKeys.lead(clinicaId, leadId),
    enabled: habilitado,
    staleTime: 30_000,
    retry: 1,
    refetchIntervalInBackground: false,
    refetchInterval: (query) => habilitado && query.state.data?.some(
      (atribuicao) => atribuicao.meta_match_status === 'pending',
    ) ? 30_000 : false,
    queryFn: async ({ signal }): Promise<LeadMetaAdAttribution[]> => {
      // Refetch manual também passa por esta validação para não consultar sem clínica/lead.
      if (!contextoValido) throw new Error('Abra novamente os detalhes deste lead para consultar a origem.');

      // RPC get_lead_meta_ad_attributions: SELECT de até 20 registros de lead_ad_attributions.
      // Campos: referência/título/URL/plataforma e datas; IDs/nomes Meta de conta, anúncio,
      // conjunto e campanha; status e data de identificação. Filtros: p_clinica_id + p_lead_id.
      // Riscos: dados comerciais privados; a RPC exige usuário autorizado e não retorna
      // ctwa_clid, whatsapp_message_id, tokens, payloads nem erros internos do worker.
      const { data, error } = await supabase.rpc('get_lead_meta_ad_attributions' as never, {
        p_clinica_id: clinicaId,
        p_lead_id: leadId,
      } as never).abortSignal(signal);

      // Não mostra a mensagem crua da API, que pode conter detalhes internos do banco.
      if (error) throw new Error('Não foi possível carregar a origem do anúncio. Tente atualizar.');
      const resultado = atribuicoesSchema.safeParse(data ?? []);
      if (!resultado.success) throw new Error('Os dados da origem estão incompletos. Tente atualizar.');
      return resultado.data;
    },
  });

  return { ...consulta, contextoValido };
}
